import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/currency-input'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

type ZipFlatRateZone = Tables<'zip_flat_rate_zones'>
type ZipFlatRateRule = Tables<'zip_flat_rate_rules'>
type Vehicle = Tables<'vehicles'>

export interface ZipRuleWithZone extends ZipFlatRateRule {
  zip_zone: ZipFlatRateZone
}

const ruleSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  zip_zone_id: z.string().uuid('Select a ZIP zone'),
  flat_rate: z.coerce.number().min(1, 'Flat rate must be at least $1'),
  apply_to_all_vehicles: z.boolean(),
  vehicle_ids: z.array(z.string()).optional(),
  priority: z.coerce.number().int().min(0),
  is_active: z.boolean().default(true),
})

type RuleFormData = z.infer<typeof ruleSchema>

interface ZipRuleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rule: ZipRuleWithZone | null
  zones: ZipFlatRateZone[]
  vehicles: Vehicle[]
  accountId: string
}

export function ZipRuleDialog({
  open,
  onOpenChange,
  rule,
  zones,
  vehicles,
  accountId,
}: ZipRuleDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!rule

  const form = useForm<RuleFormData>({
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      name: '',
      description: '',
      zip_zone_id: '',
      flat_rate: 0,
      apply_to_all_vehicles: true,
      vehicle_ids: [],
      priority: 0,
      is_active: true,
    },
  })

  const applyToAll = form.watch('apply_to_all_vehicles')

  useEffect(() => {
    if (open) {
      if (rule) {
        form.reset({
          name: rule.name,
          description: rule.description || '',
          zip_zone_id: rule.zip_zone_id,
          flat_rate: rule.flat_rate,
          apply_to_all_vehicles: !rule.vehicle_ids,
          vehicle_ids: rule.vehicle_ids || [],
          priority: rule.priority,
          is_active: rule.is_active,
        })
      } else {
        form.reset({
          name: '',
          description: '',
          zip_zone_id: zones[0]?.id || '',
          flat_rate: 0,
          apply_to_all_vehicles: true,
          vehicle_ids: [],
          priority: 0,
          is_active: true,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rule, zones])

  const persist = async (data: RuleFormData) => {
    const payload = {
      name: data.name,
      description: data.description || null,
      zip_zone_id: data.zip_zone_id,
      flat_rate: data.flat_rate,
      vehicle_ids: data.apply_to_all_vehicles ? null : data.vehicle_ids ?? [],
      priority: data.priority,
      is_active: data.is_active,
    }

    if (rule) {
      const { error } = await supabase
        .from('zip_flat_rate_rules')
        .update(payload)
        .eq('id', rule.id)
      if (error) throw error
    } else {
      const { error } = await supabase
        .from('zip_flat_rate_rules')
        .insert({ account_id: accountId, ...payload })
      if (error) throw error
    }
  }

  const mutation = useMutation({
    mutationFn: persist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zip-flat-rate-rules', accountId] })
      toast({
        title: isEditing ? 'Rule updated' : 'Rule created',
        description: 'The ZIP flat rate rule has been saved successfully.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const onSubmit = (data: RuleFormData) => mutation.mutate(data)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit ZIP Flat Rate' : 'Create ZIP Flat Rate'}</DialogTitle>
          <DialogDescription>
            Set a fixed price for trips between any hub and this ZIP zone (either direction).
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Buckhead — Sedan" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="zip_zone_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP Zone</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select zone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {zones.map((zone) => (
                        <SelectItem key={zone.id} value={zone.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: zone.color }}
                            />
                            {zone.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The non-hub end of the trip must fall in this zone's ZIP codes.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="flat_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Flat Rate Price</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      className="w-40"
                    />
                  </FormControl>
                  <FormDescription>
                    This fixed price replaces the distance-based calculation
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apply_to_all_vehicles"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Apply to All Vehicles</FormLabel>
                    <FormDescription>
                      Or select specific vehicles this rate applies to
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {!applyToAll && (
              <FormField
                control={form.control}
                name="vehicle_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Vehicles</FormLabel>
                    <div className="border rounded-lg p-3 max-h-[150px] overflow-y-auto space-y-2">
                      {vehicles.length > 0 ? (
                        vehicles.map((vehicle) => (
                          <div key={vehicle.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`zip-veh-${vehicle.id}`}
                              checked={field.value?.includes(vehicle.id)}
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), vehicle.id]
                                  : (field.value || []).filter((id) => id !== vehicle.id)
                                field.onChange(newValue)
                              }}
                            />
                            <label
                              htmlFor={`zip-veh-${vehicle.id}`}
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              {vehicle.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No vehicles available</p>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} className="w-24" {...field} />
                  </FormControl>
                  <FormDescription>
                    Lower number = higher priority when multiple rules match.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>Inactive rules won't be applied</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
